import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { ALL_NAV_ITEMS, ENCYCLOPEDIA_MENU } from "./navConstants";
import { NavSettings } from "@/hooks/useNavSettings";

interface MobileMenuProps {
  navSettings: NavSettings | undefined;
  isOpen: boolean;
}

export function MobileMenu({ navSettings, isOpen }: MobileMenuProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { nav_order: navOrder, nav_visibility: navVisibility } =
    navSettings || {};

  const isEncyclopediaVisible = navVisibility?.encyclopedia !== false;

  const visibleEncyclopediaMenu = React.useMemo(() => {
    return ENCYCLOPEDIA_MENU.filter((item) => {
      if (navVisibility) {
        return navVisibility[item.key as keyof typeof navVisibility] !== false;
      }
      return true;
    });
  }, [navVisibility]);

  const renderLink = (key: string) => {
    const item = ALL_NAV_ITEMS[key];
    if (!item) return null;
    if (
      navVisibility &&
      navVisibility[key as keyof typeof navVisibility] === false
    )
      return null;

    const isActive =
      location.pathname === item.path ||
      (item.path !== "/" && location.pathname.startsWith(item.path));

    return (
      <Link
        key={item.key}
        to={item.path}
        className={cn(
          "flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-3 text-sm font-medium transition-colors",
          isActive
            ? "bg-foreground text-background"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        )}
      >
        {item.icon}
        {t(item.labelKey, item.defaultLabel)}
      </Link>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="lg:hidden bg-gradient-to-b from-background/85 to-background/75 backdrop-blur-lg border-b border-border/40 rounded-b-2xl shadow-xl shadow-black/10 animate-in slide-in-from-top duration-200 relative before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent">
      <div className="flex flex-col gap-1 p-4">
        {navOrder?.map((key) => {
          if (key === "encyclopedia") {
            if (!isEncyclopediaVisible || visibleEncyclopediaMenu.length === 0)
              return null;
            return (
              <div key="encyclopedia" className="mt-2 mb-1">
                <div className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <BookOpen className="size-3" />
                  {t("nav.encyclopedia", "百科")}
                </div>
                <div className="flex flex-col gap-1 pl-4">
                  {visibleEncyclopediaMenu.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.key}
                        to={item.path}
                        className={cn(
                          "flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-foreground text-background"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground",
                        )}
                      >
                        {item.icon}
                        {t(item.labelKey, item.defaultLabel)}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          }

          if (key === "encyclopedia_articles" || key === "encyclopedia_faqs") {
            return null;
          }

          return renderLink(key);
        })}
      </div>
    </div>
  );
}
